import { observer } from "mobx-react-lite";

import { useAsync } from "@bps/hooks";
import {
  CalendarEventStatus,
  CalendarEventStatusText
} from "@libs/gateways/booking/BookingGateway.dtos.ts";
import { nameOfFactory } from "@libs/utils/name-of.utils.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { withFetch } from "@ui-components/data-fetcher/DataFetcher.tsx";
import { FilterBar } from "@ui-components/filter-bar/FilterBar.tsx";
import { FilterBarProps } from "@ui-components/filter-bar/FilterBar.types.ts";
import { showUnlessPrintingStyles } from "@ui-components/printing/Print.styles.ts";

import { durationOptions } from "../utils.ts";
import { AppointmentInformationPivotTabs } from "./AppointmentInformationPivotTabs.tsx";
import { AppointmentInformationModalInnerProps } from "./AppointmentInformationPivotTabs.types.ts";

export type ApptinfoFilterType = {
  claim?: string;
  apptDate?: Date;
  status?: string[];
  apptType?: string[];
  providerId?: string;
  durationRange?: string;
};

const AppointmentInformationModalInnerBase: React.FC<AppointmentInformationModalInnerProps> =
  observer(props => {
    const { booking, core } = useStores();

    const appointmentStatusOptions =
      booking.ref.appointmentStatuses.keyTextValues;

    const statusOptions = appointmentStatusOptions.concat([
      {
        key: CalendarEventStatus.Cancelled,
        text: CalendarEventStatusText.CANCELLED
      }
    ]);

    const { data: apptTypes = [] } = useAsync(async () => {
      return await booking.loadAppointmentTypes();
    });

    const nameOf = nameOfFactory<ApptinfoFilterType>();
    const renderFilterItems: FilterBarProps["items"] = [
      {
        name: nameOf("apptType"),
        type: "optionsSelect",
        stickItem: true,
        props: {
          options: apptTypes.map(data => ({
            key: data.id,
            text: data.name
          })),
          placeholder: "Search appointments",
          styles: { root: { width: 300 } }
        }
      }
    ];

    if (!props.claimId && core.isNZTenant) {
      renderFilterItems.push({
        name: nameOf("claim"),
        type: "searchBox",
        props: {
          placeholder: "Search claim no"
        }
      });
    }

    renderFilterItems.push(
      {
        type: "datePicker",
        name: nameOf("apptDate"),
        props: {
          allowClear: true,
          placeholder: "Date"
        }
      },
      {
        type: "optionsSelect",
        name: nameOf("status"),
        props: {
          placeholder: "Status",
          label: "Status",
          options: statusOptions,
          hideSearchOption: true,
          calloutWidth: 130,
          multiSelect: true
        }
      },
      {
        type: "optionsSelect",
        name: nameOf("durationRange"),
        props: {
          placeholder: "Duration",
          label: "Duration",
          options: durationOptions,
          hideSearchOption: true,
          calloutWidth: 150,
          multiSelect: false
        }
      },
      {
        type: "bookableUsersSelect",
        name: nameOf("providerId"),
        props: {
          multiSelect: false,
          usersFilter: { showOnCalendar: true },
          placeholder: "Providers",
          calloutWidth: 250
        }
      }
    );

    return (
      <FilterBar<ApptinfoFilterType>
        items={renderFilterItems}
        styles={showUnlessPrintingStyles}
      >
        {() => <AppointmentInformationPivotTabs {...props} />}
      </FilterBar>
    );
  });

export const AppointmentInformationModalInner = withFetch(
  root =>
    root.core.isNZTenant
      ? root.practice.ref.accProviderContractTypes.load()
      : Promise.resolve(),
  AppointmentInformationModalInnerBase
);
